// 标签式列表（Focus Carousel）状态：按当前 rootMenu 隔离
import { getPortalContentDetail, getPortalContents } from '@/api/portal'
import {
  buildContentFetchTargets,
  buildPortalFocusNavItems,
  filterFetchTargets,
  normalizeContentList,
  normalizeNewsDetail
} from '@/components/modules/news/utils/newsNormalize'
import { findContentByRouteKey } from '@/components/home/utils/homeSectionItemLink'
import {
  getNewsSectionLoadingKey,
  resolveNewsFetchPayload
} from '@/components/modules/news/utils/newsPages'

let sectionFetchSeqMap = new Map()
const inflightSectionRequests = new Map()
const detailFetchSeqMap = new Map()

function emptyRootBucket() {
  return {
    items: [],
    navItems: [],
    fetchTargets: [],
    sectionLoaded: {},
    categoryLoading: {},
    metaLoaded: false
  }
}

function snapshotRootBucket(state) {
  return {
    items: state.items,
    navItems: state.navItems,
    fetchTargets: state.fetchTargets,
    sectionLoaded: { ...state.sectionLoaded },
    categoryLoading: { ...state.categoryLoading },
    metaLoaded: state.metaLoaded
  }
}

function writeThroughRootCache(state) {
  if (!state.scopedRootKey) return
  state.rootCaches = {
    ...state.rootCaches,
    [state.scopedRootKey]: snapshotRootBucket(state)
  }
}

function findListItem(state, id) {
  const key = String(id)
  const fromActive = state.items.find((item) => String(item.id) === key)
  if (fromActive) return fromActive

  for (const bucket of Object.values(state.rootCaches || {})) {
    const found = (bucket.items || []).find((item) => String(item.id) === key)
    if (found) return found
  }
  return null
}

function sortNewsItems(a, b) {
  const dateCompare = (b.publishedAt || '').localeCompare(a.publishedAt || '')
  if (dateCompare !== 0) return dateCompare
  return a.sortOrder - b.sortOrder
}

export default {
  namespaced: true,
  state: {
    items: [],
    navItems: [],
    fetchTargets: [],
    activeRootMenu: null,
    categoryLoading: {},
    sectionLoaded: {},
    metaLoaded: false,
    scopedRootKey: '',
    rootCaches: {},
    loading: false,
    detailMap: {},
    detailLoadingMap: {}
  },
  getters: {
    allItems(state) {
      return state.items
    },
    navItems(state) {
      return state.navItems
    },
    getByCategory: (state) => (category) => {
      if (!category) return []
      return state.items.filter((item) => item.category === category)
    },
    isCategoryLoading: (state) => (key) => Boolean(state.categoryLoading[key]),
    getBySlug: (state) => (routeKey) => findContentByRouteKey(state.items, routeKey) || null,
    getById: (state) => (id) => findListItem(state, id),
    getDetailById: (state) => (id) => {
      if (!id) return null
      const key = String(id)
      return Object.prototype.hasOwnProperty.call(state.detailMap, key)
        ? state.detailMap[key]
        : undefined
    },
    isDetailLoading: (state) => (id) => Boolean(state.detailLoadingMap[String(id)])
  },
  mutations: {
    SET_ITEMS(state, items) {
      state.items = items || []
      writeThroughRootCache(state)
    },
    SET_NAV_ITEMS(state, navItems) {
      state.navItems = navItems || []
      writeThroughRootCache(state)
    },
    SET_FETCH_TARGETS(state, fetchTargets) {
      state.fetchTargets = fetchTargets || []
      writeThroughRootCache(state)
    },
    SET_ACTIVE_ROOT_MENU(state, rootMenu) {
      state.activeRootMenu = rootMenu || null
    },
    SET_META_LOADED(state, loaded) {
      state.metaLoaded = loaded
    },
    SET_SCOPED_ROOT_KEY(state, key) {
      state.scopedRootKey = key || ''
    },
    SAVE_ROOT_CACHE(state, { key, bucket }) {
      if (!key) return
      state.rootCaches = {
        ...state.rootCaches,
        [key]: bucket || emptyRootBucket()
      }
    },
    RESTORE_ROOT_CACHE(state, { key, rootMenu }) {
      const bucket = state.rootCaches[key] || emptyRootBucket()
      state.items = bucket.items || []
      state.navItems = bucket.navItems || []
      state.fetchTargets = bucket.fetchTargets || []
      state.sectionLoaded = { ...(bucket.sectionLoaded || {}) }
      state.categoryLoading = { ...(bucket.categoryLoading || {}) }
      state.metaLoaded = Boolean(bucket.metaLoaded)
      state.scopedRootKey = key || ''
      state.activeRootMenu = rootMenu || null
    },
    INIT_ROOT_BUCKET(state, { key, rootMenu, navItems, fetchTargets }) {
      state.items = []
      state.navItems = navItems || []
      state.fetchTargets = fetchTargets || []
      state.sectionLoaded = {}
      state.categoryLoading = {}
      state.metaLoaded = true
      state.scopedRootKey = key || ''
      state.activeRootMenu = rootMenu || null
      writeThroughRootCache(state)
    },
    SET_CATEGORY_LOADING(state, { key, loading }) {
      state.categoryLoading = {
        ...state.categoryLoading,
        [key]: loading
      }
    },
    SET_SECTION_LOADED(state, { key, loaded }) {
      state.sectionLoaded = {
        ...state.sectionLoaded,
        [key]: loaded
      }
      writeThroughRootCache(state)
    },
    RESET_SECTION_CACHE(state) {
      state.items = []
      state.sectionLoaded = {}
      state.categoryLoading = {}
      state.detailMap = {}
      state.detailLoadingMap = {}
    },
    RESET_FOR_LOCALE(state) {
      state.metaLoaded = false
      state.scopedRootKey = ''
      state.rootCaches = {}
      state.fetchTargets = []
      state.navItems = []
      state.activeRootMenu = null
      state.items = []
      state.categoryLoading = {}
      state.sectionLoaded = {}
      state.detailMap = {}
      state.detailLoadingMap = {}
    },
    CLEAR_DETAIL_CACHE(state) {
      state.detailMap = {}
      state.detailLoadingMap = {}
    },
    SET_LOADING(state, loading) {
      state.loading = loading
    },
    SET_DETAIL(state, { id, detail }) {
      state.detailMap = {
        ...state.detailMap,
        [String(id)]: detail
      }
    },
    SET_DETAIL_LOADING(state, { id, loading }) {
      state.detailLoadingMap = {
        ...state.detailLoadingMap,
        [String(id)]: loading
      }
    },
    PATCH_ITEM_COVER(state, { id, cover }) {
      if (!id || !cover) return
      const key = String(id)
      let changed = false
      state.items = (state.items || []).map((item) => {
        if (String(item.id) !== key) return item
        changed = true
        return { ...item, cover }
      })
      if (changed) writeThroughRootCache(state)
    }
  },
  actions: {
    resetForLocaleChange({ commit }) {
      commit('RESET_FOR_LOCALE')
    },
    async ensureNewsMeta({ commit, dispatch, state, rootState }, rootMenu = null) {
      const locale = rootState.locale.current
      if (!locale) return []

      const resolvedRoot = rootMenu || state.activeRootMenu
      if (!resolvedRoot) return []

      const scopedRootKey = String(resolvedRoot.id)
      if (state.scopedRootKey === scopedRootKey && state.metaLoaded && state.fetchTargets.length) {
        return state.fetchTargets
      }

      if (state.scopedRootKey && state.scopedRootKey !== scopedRootKey) {
        commit('SAVE_ROOT_CACHE', {
          key: state.scopedRootKey,
          bucket: snapshotRootBucket(state)
        })
      }

      const cached = state.rootCaches[scopedRootKey]
      if (cached?.metaLoaded) {
        commit('RESTORE_ROOT_CACHE', { key: scopedRootKey, rootMenu: resolvedRoot })
        return cached.fetchTargets || []
      }

      const menus = await dispatch('nav/ensureRawMenus', null, { root: true })
      const targets = buildContentFetchTargets(menus, resolvedRoot)
      commit('INIT_ROOT_BUCKET', {
        key: scopedRootKey,
        rootMenu: resolvedRoot,
        navItems: buildPortalFocusNavItems(menus, resolvedRoot),
        fetchTargets: targets
      })
      return targets
    },
    async fetchNewsSection({ commit, dispatch, state, rootState }, payload = {}) {
      const locale = rootState.locale.current
      if (!locale) return

      const { force = false, rootMenu = null, ...fetchPayload } = payload
      const resolvedRoot = rootMenu || state.activeRootMenu
      const loadingKey = getNewsSectionLoadingKey(fetchPayload)
      const activeScopedKey = resolvedRoot ? String(resolvedRoot.id) : state.scopedRootKey
      if (!force && state.scopedRootKey === activeScopedKey && state.sectionLoaded[loadingKey]) return

      const requestKey = JSON.stringify({ locale, rootMenuId: resolvedRoot?.id, ...fetchPayload })
      if (inflightSectionRequests.has(requestKey)) {
        return inflightSectionRequests.get(requestKey)
      }

      const task = (async () => {
        const targets = await dispatch('ensureNewsMeta', resolvedRoot)
        if (!force && state.sectionLoaded[loadingKey]) return
        const filtered = filterFetchTargets(targets, fetchPayload)
        if (!filtered.length) {
          commit('SET_SECTION_LOADED', { key: loadingKey, loaded: true })
          return
        }

        const seq = (sectionFetchSeqMap.get(loadingKey) || 0) + 1
        sectionFetchSeqMap.set(loadingKey, seq)

        commit('SET_CATEGORY_LOADING', { key: loadingKey, loading: true })
        try {
          const results = await Promise.all(
            filtered.map((target) =>
              getPortalContents({
                type: target.contentType,
                menuId: target.menuId,
                locale
              }).then((contents) => ({ target, contents }))
            )
          )
          if (sectionFetchSeqMap.get(loadingKey) !== seq) return

          const normalized = []
          for (const { target, contents } of results) {
            normalized.push(...normalizeContentList(contents, target))
          }

          const categories = new Set(filtered.map((target) => target.category))
          const preserved = fetchPayload.category
            ? state.items.filter((item) => item.category !== fetchPayload.category)
            : state.items.filter((item) => !categories.has(item.category))
          commit('SET_ITEMS', [...preserved, ...normalized].sort(sortNewsItems))
          commit('SET_SECTION_LOADED', { key: loadingKey, loaded: true })
        } finally {
          if (sectionFetchSeqMap.get(loadingKey) === seq) {
            commit('SET_CATEGORY_LOADING', { key: loadingKey, loading: false })
          }
        }
      })()

      inflightSectionRequests.set(requestKey, task)
      try {
        await task
      } finally {
        inflightSectionRequests.delete(requestKey)
      }
    },
    async reloadByPath({ dispatch, getters }, payload) {
      const path = typeof payload === 'string' ? payload : payload?.path
      const force = typeof payload === 'object' && payload ? Boolean(payload.force) : false
      const rootMenu = typeof payload === 'object' && payload ? payload.rootMenu || null : null
      if (rootMenu) {
        await dispatch('ensureNewsMeta', rootMenu)
      }
      const navItems = getters.navItems
      const resolved = resolveNewsFetchPayload(path, {
        navItems,
        rootMenu,
        ...(typeof payload === 'object' ? payload : {})
      })
      if (!resolved) return Promise.resolve()
      return dispatch('fetchNewsSection', {
        ...resolved,
        rootMenu,
        force
      })
    },
    async fetchNewsDetail({ commit, rootState, state }, id) {
      const locale = rootState.locale.current
      if (!locale || !id) return null

      const key = String(id)
      if (Object.prototype.hasOwnProperty.call(state.detailMap, key)) {
        return state.detailMap[key]
      }

      const seq = (detailFetchSeqMap.get(key) || 0) + 1
      detailFetchSeqMap.set(key, seq)

      commit('SET_DETAIL_LOADING', { id: key, loading: true })
      try {
        const data = await getPortalContentDetail(id, locale)
        if (detailFetchSeqMap.get(key) !== seq) return null

        const listItem = findListItem(state, key)
        const detail = normalizeNewsDetail(data, listItem)
        commit('SET_DETAIL', { id: key, detail })
        return detail
      } catch (error) {
        if (detailFetchSeqMap.get(key) !== seq) return null
        commit('SET_DETAIL', { id: key, detail: null })
        throw error
      } finally {
        if (detailFetchSeqMap.get(key) === seq) {
          commit('SET_DETAIL_LOADING', { id: key, loading: false })
        }
      }
    }
  }
}
